import { DataButton } from "../Button";
import { Icon } from "../Icon";
import { InputContainer, InputOption, InputSelect } from "../Input";
import type { TableCursorPagination } from "./pagination";

type OffsetControls = {
  pageIndex: number;
  pageSize: number;
  isLoading?: boolean;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
} & (
  | { mode: "offset"; pageCount: number }
  | { mode: "offset-no-total"; hasNextPage: boolean }
);

export function TablePaginationControls({
  pagination,
  pageSizeOptions,
  paginationLabel,
}: {
  pagination: OffsetControls | TableCursorPagination;
  pageSizeOptions: number[];
  paginationLabel: string;
}) {
  const cursor = pagination.mode === "cursor";
  const isLoading = pagination.isLoading;
  const hasPreviousPage = cursor
    ? pagination.hasPreviousPage
    : pagination.pageIndex > 0;
  const hasNextPage =
    pagination.mode === "offset"
      ? pagination.pageIndex + 1 < pagination.pageCount
      : pagination.hasNextPage;
  const firstPage = cursor
    ? pagination.firstPage
    : {
        isCurrent: pagination.pageIndex === 0,
        onClick: () => pagination.onPageChange(0),
      };

  return (
    <nav
      aria-label="Table pagination"
      aria-busy={isLoading}
      className="flex shrink-0 items-center gap-2 rounded-b-lg bg-zinc-50 p-4 dark:border-t dark:border-zinc-800 dark:bg-zinc-900/50"
    >
      <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] dark:border-zinc-700 dark:bg-zinc-800">
        <InputSelect
          aria-label={paginationLabel}
          value={pagination.pageSize}
          disabled={isLoading}
          onChange={(event) =>
            pagination.onPageSizeChange(Number(event.target.value))
          }
          className="text-sm font-medium dark:bg-zinc-800 dark:text-zinc-100"
        >
          {[...new Set([pagination.pageSize, ...pageSizeOptions])]
            .sort((a, b) => a - b)
            .map((size) => (
              <InputOption key={size} value={size} className="dark:bg-zinc-800">
                {size}
              </InputOption>
            ))}
        </InputSelect>
      </InputContainer>
      <span className="mr-auto inline-block text-sm dark:text-zinc-400">
        {paginationLabel}
      </span>
      {firstPage && (
        <DataButton
          aria-label="First page"
          title="First page"
          className="p-1 dark:border-zinc-800 dark:text-zinc-400"
          border
          onClick={firstPage.onClick}
          disabled={isLoading || firstPage.isCurrent}
        >
          <Icon icon="CHEVRON_DOUBLE_LEFT" />
        </DataButton>
      )}
      <DataButton
        aria-label="Previous page"
        title="Previous page"
        className="p-1 dark:border-zinc-800 dark:text-zinc-400"
        border
        disabled={isLoading || !hasPreviousPage}
        onClick={() => {
          if (cursor) pagination.onPreviousPage();
          else pagination.onPageChange(Math.max(0, pagination.pageIndex - 1));
        }}
      >
        <Icon icon="CHEVRON_LEFT" />
      </DataButton>
      {!cursor && (
        <span className="flex items-center gap-1 px-2 dark:text-zinc-300">
          <span>Page</span>
          <strong>
            {pagination.pageIndex + 1}
            {pagination.mode === "offset" && (
              <>
                {" "}
                <span className="font-normal dark:text-zinc-500">of</span>{" "}
                {pagination.pageCount.toLocaleString()}
              </>
            )}
          </strong>
        </span>
      )}
      <DataButton
        aria-label="Next page"
        title="Next page"
        className="p-1 dark:border-zinc-800 dark:text-zinc-400"
        border
        disabled={isLoading || !hasNextPage}
        onClick={() => {
          if (cursor) pagination.onNextPage();
          else if (pagination.mode === "offset-no-total")
            pagination.onPageChange(pagination.pageIndex + 1);
          else
            pagination.onPageChange(
              Math.min(pagination.pageCount - 1, pagination.pageIndex + 1),
            );
        }}
      >
        <Icon icon="CHEVRON_RIGHT" />
      </DataButton>
      {pagination.mode === "offset" && (
        <DataButton
          aria-label="Last page"
          title="Last page"
          className="p-1 dark:border-zinc-800 dark:text-zinc-400"
          border
          disabled={isLoading || !hasNextPage}
          onClick={() =>
            pagination.onPageChange(Math.max(0, pagination.pageCount - 1))
          }
        >
          <Icon icon="CHEVRON_DOUBLE_RIGHT" />
        </DataButton>
      )}
    </nav>
  );
}
